import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "antd";
import { Form, Formik } from "formik";
import { Select as FormikSelect } from "formik-antd";
import { equals } from "ramda";
import * as Yup from "yup";

import CommonInput from "../../../../../../components/commonFields";
import CustomButton from "../../../../../../components/customButton";
import CustomLabel from "../../../../../../components/customLabel";

import "./styles.scss";
import { MultipleFormikSelect } from "../../../../../../components/select/multipleSelect";
import CommonService from "../../../../../../service/CommonService";
import { DEBOUNCE_TIME } from "../../../../../../constants/constants";
import { debounceAsync } from "../../../../../../helper/helpers";
import { ERRORS } from "../../../../constants";

const commonService = new CommonService();

const { Option } = FormikSelect;

let saveAs = [
  {
    key: "existing",
    value: "Existing Template",
  },
  {
    key: "new",
    value: "New Template",
  },
];

const EditTemplateModal = (props) => {
  const { formValues, tempName, name, displayShowAs, type } = props;
  const isIvrSmsMessage = ["message", "ivrMessage"].includes(name);

  const schema = Yup.object().shape({
    template: Yup.string()
      .nullable()
      .when("saveAs", {
        is: (value) => value === "new",
        then: Yup.string()
          .nullable()
          .required("Template name is required"),
      }),
    ...(displayShowAs
      ? { accounts: Yup.array().min(1, "At least 1 account is mandatory") }
      : {}),
  });

  const [state, setState] = useState({
    initialValues: {
      template: "",
      saveAs: "existing",
      showAs: props.showAs,
      accounts: props.selectedAccounts,
    },
    templateExists: false,
  });

  useEffect(
    () => {
      setState((st) => ({
        ...st,
        initialValues: {
          ...st.initialValues,
          template: props.templateName,
          accounts: props.selectedAccounts,
        },
      }));
    },
    [props.templateName, props.selectedAccounts]
  );

  const debouncedCheckTemplateExists = useCallback(
    debounceAsync(commonService.checkTemplateExists, DEBOUNCE_TIME, false),
    [commonService.checkTemplateExists]
  );

  const handleChange = (templateName, accounts) => {
    // Call the API to check if the template name already exists.
    // This check is performed only if:
    // 1. The template name is not empty.
    // 2. Accounts are selected when the user is either OU or TS.
    // 3. The message type is either SMS or IVR.
    if (
      !templateName.trim() ||
      (!accounts?.length && displayShowAs) ||
      !isIvrSmsMessage
    ) {
      setState((prevState) => ({
        ...prevState,
        templateExists: false,
      }));
      return;
    }
    const accountIdsSelected = displayShowAs
      ? accounts.map((value) => value.practiceId || "all")
      : [];
    debouncedCheckTemplateExists(templateName, type, accountIdsSelected)
      .then((response) => {
        if (response.data.exists) {
          setState((prevState) => ({
            ...prevState,
            templateExists: response.data.exists,
          }));
        } else {
          setState((prevState) => ({
            ...prevState,
            templateExists: false,
          }));
        }
      })
      .catch((err) => {
        console.error(err);
        setState((st) => ({
          ...st,
          templateExists: ERRORS.TEMPLATE.CHECK_NAME_API_ERROR,
        }));
      });
  };

  return (
    <Modal
      className="edit-template-modal"
      title={props.title}
      mask={false}
      visible={props.isVisible}
      centered
      footer={null}
    >
      <Formik
        enableReinitialize
        initialValues={state.initialValues}
        validationSchema={schema}
        onSubmit={(values, actions) => {
          props.handleSubmit(this.submitpatientsearch(values));
        }}
      >
        {({
          touched,
          errors,
          isValid,
          values,
          setFieldValue,
          validateForm,
        }) => {
          const isSaveTemplateButtonDisabled =
            !isValid ||
            (displayShowAs && values.saveAs === "existing"
              ? equals(state.initialValues.accounts, values.accounts) &&
                formValues[name] === formValues[tempName]
              : false) ||
            (values.saveAs === "new" && state.templateExists);

          return (
            <Form className="fields-wrapper">
              <section className="form-row">
                <CustomLabel title="Save as" />
                <FormikSelect
                  name="saveAs"
                  className={`
                    common-select-field ${
                      touched.saveAs && errors.saveAs ? "error-box" : ""
                    }`}
                  placeholder="Save as"
                  getPopupContainer={(trigger) => trigger.parentNode}
                  defaultValue={state.initialValues.saveAs}
                  onChange={(value) => {
                    if (value === "new") {
                      setFieldValue("template", "");
                    } else {
                      setFieldValue("template", props.templateName);
                      setFieldValue("showAs", props.showAs);
                    }
                    setTimeout(() => {
                      validateForm();
                    }, 0);
                    props.handleRemoveError();
                    setState((prevState) => ({
                      ...prevState,
                      templateExists: false,
                    }));
                  }}
                >
                  {saveAs.map((item) => (
                    <Option key={item.key} value={item.key}>
                      {item.value}
                    </Option>
                  ))}
                </FormikSelect>
              </section>
              <section className="form-row">
                <CustomLabel title="Template Name" required />
                <CommonInput
                  placeholder="Enter Template Name"
                  className={`custom-form-input ${
                    state.error ? "error-box" : ""
                  }`}
                  value={values.template}
                  disabled={values.saveAs === "existing"}
                  onChange={(e) => {
                    setFieldValue("template", e.target.value);
                    if (values.saveAs === "new") {
                      handleChange(e.target.value, values.accounts);
                    }
                  }}
                />
                {state.templateExists &&
                  values.saveAs === "new" && (
                    <div className="error-label mob-margin save-template-error">
                      {ERRORS.TEMPLATE.NAME_EXISTS}.
                    </div>
                  )}
              </section>
              <section className="form-row">
                {displayShowAs && (
                  <MultipleFormikSelect
                    data={props.accountsList}
                    name="accounts"
                    optionLabelProp="name"
                    optionValueProp="id"
                    placeholder="Select account name"
                    required
                    title="Add to Accounts"
                    infiniteScroll
                    showAll={true}
                    loadMoreData={props.fetchAccounts}
                    onChange={(selectedAccounts) => {
                      if (values.saveAs === "new") {
                        handleChange(values.template, selectedAccounts);
                      }
                    }}
                  />
                )}
              </section>
              <section className="form-actions">
                <div className="error-label edit-modal-error">
                  {props.responseError}
                </div>

                <section className="form-buttons">
                  <CustomButton
                    htmlType="submit"
                    type="primary"
                    className={
                      isSaveTemplateButtonDisabled ? "disable-button" : ""
                    }
                    disabled={isSaveTemplateButtonDisabled}
                    title={props.submitButtonText}
                    onClick={() => props.handleDelete(values)}
                  />
                  <CustomButton
                    className={`ml-8 cancel-button`}
                    onClick={props.handleCancel}
                    title={props.cancelButtonText}
                    key="cancel"
                  />
                </section>
              </section>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default EditTemplateModal;
